import React from 'react';

import {
  SlurmCommandState,
  type SlurmCommandStatus,
  SlurmCommandType,
} from '../../generated/graphql';

type StatusConfig = {
  label: string;
  tooltip: string;
};

export const SLURM_COMMAND_PICKUP_WARNING_MS = 5 * 60 * 1000; // 5 minutes

function commandLabel(command: SlurmCommandType) {
  switch (command) {
    case SlurmCommandType.Cancel:
      return 'Cancel';
    case SlurmCommandType.Requeue:
      return 'Requeue';
    default: {
      const _exhaustiveCheck: never = command;
      throw new Error(`Unhandled command type: ${_exhaustiveCheck}`);
    }
  }
}

function isStalePending(createdAt: string, now: Date) {
  const createdAtMs = new Date(createdAt).getTime();
  return (
    Number.isFinite(createdAtMs) &&
    now.getTime() - createdAtMs > SLURM_COMMAND_PICKUP_WARNING_MS
  );
}

export function getSlurmCommandStatusConfig(
  status: SlurmCommandStatus,
  now = new Date()
): StatusConfig {
  const label = commandLabel(status.command);
  const lowerLabel = label.toLowerCase();

  switch (status.state) {
    case SlurmCommandState.Pending:
      if (isStalePending(status.createdAt, now)) {
        return {
          label: `${label} not picked up`,
          tooltip: `W&B queued the ${lowerLabel} command, but the Slurm daemon has not picked it up after several minutes. Something may be wrong.`,
        };
      }
      return {
        label: `${label} requested`,
        tooltip: `W&B queued the ${lowerLabel} command. The Slurm daemon has not picked it up yet.`,
      };
    case SlurmCommandState.Leased:
      return {
        label: `${label} in progress`,
        tooltip: `The Slurm daemon picked up the ${lowerLabel} command and is sending it to Slurm.`,
      };
    case SlurmCommandState.Submitted:
      return {
        label: `${label} accepted by Slurm`,
        tooltip:
          status.command === SlurmCommandType.Cancel
            ? 'Slurm accepted the cancel command. The job will stop after Slurm processes it.'
            : 'Slurm accepted the requeue command. The job will restart after Slurm processes it.',
      };
    case SlurmCommandState.Failed:
      return {
        label: `${label} failed`,
        tooltip: `The Slurm daemon could not complete the ${lowerLabel} command.`,
      };
  }
}

interface SlurmCommandStatusIndicatorProps {
  command: SlurmCommandStatus;
}

export function SlurmCommandStatusIndicator({
  command,
}: SlurmCommandStatusIndicatorProps) {
  const config = getSlurmCommandStatusConfig(command);

  return (
    <div title={config.tooltip}>
      <span>{config.label}</span>
    </div>
  );
}
